import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { VendorService } from 'src/app/services/marketplace/vendor.service';
import { ListingService } from 'src/app/services/marketplace/listing.service';
import { ReviewService } from 'src/app/services/marketplace/review.service';
import { CouponDraft, CouponService } from 'src/app/services/marketplace/coupon.service';
import { ToastService } from 'src/app/services/toast.service';
import {
  ConnectAccountStatus,
  Coupon,
  Listing,
  Review,
  Vendor,
  VendorStats,
} from 'src/app/services/marketplace/marketplace.models';

type DashboardTab = 'overview' | 'items' | 'reviews' | 'coupons' | 'profile';

/** Uploads are shrunk in the browser first, so big photos just work. */
const PICTURE_LIMITS = {
  banner: { width: 1920, height: 640 },
  logo: { width: 512, height: 512 },
};
/** Pictures already this small (and PNG / JPG / WebP) are sent as they are. */
const PICTURE_SEND_AS_IS_BYTES = 1.5 * 1024 * 1024;
/** The server's limit; the fallback when shrinking fails. */
const PICTURE_SERVER_MAX_BYTES = 5 * 1024 * 1024;
const PICTURE_SERVER_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
/** Give up on shrinking after this long and use the original if it can be. */
const PICTURE_PREPARE_TIMEOUT_MS = 10000;

@Component({
  selector: 'app-vendor-dashboard',
  templateUrl: './vendor-dashboard.component.html',
  styleUrls: ['./vendor-dashboard.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class VendorDashboardComponent implements OnInit, OnDestroy {
  vendor: Vendor | null = null;
  connectStatus: ConnectAccountStatus | null = null;
  stats: VendorStats | null = null;
  /** Why the shop couldn't load: an expired sign-in, or anything else. */
  loadError: 'signin' | 'other' | null = null;
  listings: Listing[] = [];
  loading = true;
  tab: DashboardTab = 'overview';

  reviews: Review[] = [];
  /** review id -> draft response text, while the vendor is composing. */
  responseDrafts: Record<number, string> = {};
  /** review id currently being submitted. */
  respondingId: number | null = null;

  // ---- shop profile editor ----
  profileName = '';
  profileBio = '';
  /** What the logo / banner previews show: saved URL, picked file, or nothing. */
  logoPreview: string | null = null;
  bannerPreview: string | null = null;
  private picked: { logo?: File; banner?: File } = {};
  private cleared = { logo: false, banner: false };
  /** A picture is being shrunk before it can be previewed. */
  preparing: { logo: boolean; banner: boolean } = { logo: false, banner: false };
  savingProfile = false;
  /** Latest pick per slot: a newer pick replaces one still being prepared. */
  private pickSeq = { logo: 0, banner: 0 };

  readonly skeletonRows = [1, 2, 3];

  // ---- coupons (discount codes for this shop) ----
  coupons: Coupon[] = [];
  couponsLoaded = false;
  couponFormOpen = false;
  couponDraft: CouponDraft = this.emptyCouponDraft();
  couponErrors: Record<string, string> = {};
  savingCoupon = false;
  /** Coupon id with a pause / delete in flight. */
  busyCouponId: number | null = null;

  constructor(
    private vendorService: VendorService,
    private listingService: ListingService,
    private reviewService: ReviewService,
    private couponService: CouponService,
    private toaster: ToastService,
    private router: Router,
    private route: ActivatedRoute,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnDestroy(): void {
    this.revokePreviews();
  }

  ngOnInit(): void {
    // "Edit shop" on the shop page links here with #shop-profile.
    if (this.route.snapshot.fragment === 'shop-profile') this.tab = 'profile';
    this.load();
  }

  load(): void {
    this.loading = true;
    this.loadError = null;
    this.vendorService.me().subscribe({
      next: (vendor) => {
        this.vendor = vendor;

        // Name + agreement are required; payouts aren't (free-only sellers can
        // skip Stripe — the dashboard shows a "finish payout setup" bar instead).
        if (!vendor.shop_name || !vendor.terms_accepted_at) {
          this.router.navigate(['/marketplace/vendor-onboarding']);
          return;
        }
        this.resetProfile();

        this.vendorService.connectAccountStatus()
          .pipe(catchError(() => of(null)))
          .subscribe((status) => {
            this.connectStatus = status;
            this.cdr.detectChanges();
          });

        this.vendorService.myStats()
          .pipe(catchError(() => of(null)))
          .subscribe((stats) => {
            this.stats = stats;
            this.cdr.detectChanges();
          });

        this.listingService
          .list({ vendor: vendor.shop_slug, include_unpublished: '1' })
          .pipe(catchError(() => of([] as Listing[])))
          .subscribe((listings) => {
            this.listings = listings;
            this.loading = false;
            this.cdr.detectChanges();
          });

        this.reviewService
          .listForVendor(vendor.shop_slug)
          .pipe(catchError(() => of([] as Review[])))
          .subscribe((reviews) => {
            this.reviews = reviews;
            this.cdr.detectChanges();
          });
      },
      error: (err) => {
        // Never a blank page: say what went wrong.
        this.loading = false;
        this.loadError = err?.status === 401 || err?.status === 403 ? 'signin' : 'other';
        this.cdr.detectChanges();
      },
    });
  }

  setTab(tab: DashboardTab): void {
    this.tab = tab;
    if (tab === 'coupons' && !this.couponsLoaded) this.loadCoupons();
  }

  // ---- coupons -----------------------------------------------------------

  private emptyCouponDraft(): CouponDraft {
    return { code: '', discount_type: 'percent', discount_value: '', max_redemptions: null, expires_at: null };
  }

  loadCoupons(): void {
    this.couponService.list()
      .pipe(catchError(() => of([] as Coupon[])))
      .subscribe((coupons) => {
        this.coupons = coupons;
        this.couponsLoaded = true;
        this.cdr.detectChanges();
      });
  }

  /** "20% off" / "$5.00 off". */
  couponLabel(coupon: Coupon): string {
    const value = Number(coupon.discount_value) || 0;
    return coupon.discount_type === 'percent' ? `${+value.toFixed(2)}% off` : `${this.money(value)} off`;
  }

  /** "Active", "Paused", "Expired" or "Used up". */
  couponState(coupon: Coupon): 'active' | 'paused' | 'expired' | 'used' {
    if (coupon.expires_at && new Date(coupon.expires_at) <= new Date()) return 'expired';
    if (coupon.max_redemptions != null && coupon.times_redeemed >= coupon.max_redemptions) return 'used';
    return coupon.active ? 'active' : 'paused';
  }

  couponStateLabel(coupon: Coupon): string {
    return { active: 'Active', paused: 'Paused', expired: 'Expired', used: 'Used up' }[this.couponState(coupon)];
  }

  openCouponForm(): void {
    this.couponDraft = this.emptyCouponDraft();
    this.couponErrors = {};
    this.couponFormOpen = true;
  }

  saveCoupon(): void {
    if (this.savingCoupon) return;
    const draft = this.couponDraft;
    const payload: CouponDraft = {
      code: draft.code.trim().toUpperCase(),
      discount_type: draft.discount_type,
      discount_value: draft.discount_value,
      max_redemptions: draft.max_redemptions ? Number(draft.max_redemptions) : null,
      // a date picker gives "YYYY-MM-DD": the code works through the end of that day
      expires_at: draft.expires_at ? new Date(`${draft.expires_at}T23:59:59`).toISOString() : null,
    };
    this.savingCoupon = true;
    this.couponErrors = {};
    this.couponService.create(payload).subscribe({
      next: (coupon) => {
        this.savingCoupon = false;
        this.coupons = [coupon, ...this.coupons];
        this.couponFormOpen = false;
        this.toaster.showSuccess(`Code ${coupon.code} created.`, 'success');
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.savingCoupon = false;
        const body = err?.error || {};
        Object.entries(body).forEach(([field, value]) => {
          this.couponErrors[field] = Array.isArray(value) ? String(value[0]) : String(value);
        });
        if (!Object.keys(this.couponErrors).length && err?.status !== 401) {
          this.couponErrors['non_field_errors'] = 'Could not create the code. Please try again.';
        }
        this.cdr.detectChanges();
      },
    });
  }

  toggleCoupon(coupon: Coupon): void {
    if (this.busyCouponId) return;
    this.busyCouponId = coupon.id;
    this.couponService.update(coupon.id, { active: !coupon.active }).subscribe({
      next: (updated) => {
        this.coupons = this.coupons.map((c) => (c.id === updated.id ? updated : c));
        this.busyCouponId = null;
        this.cdr.detectChanges();
      },
      error: () => {
        this.busyCouponId = null;
        this.toaster.showNote('Could not change the code.', 'danger');
        this.cdr.detectChanges();
      },
    });
  }

  deleteCoupon(coupon: Coupon): void {
    if (this.busyCouponId) return;
    if (!window.confirm(`Delete the code ${coupon.code}? Buyers won't be able to use it any more.`)) return;
    this.busyCouponId = coupon.id;
    this.couponService.remove(coupon.id).subscribe({
      next: () => {
        this.coupons = this.coupons.filter((c) => c.id !== coupon.id);
        this.busyCouponId = null;
        this.cdr.detectChanges();
      },
      error: () => {
        this.busyCouponId = null;
        this.toaster.showNote('Could not delete the code.', 'danger');
        this.cdr.detectChanges();
      },
    });
  }

  /** Copies a code so the seller can paste it into a post. */
  copyCode(coupon: Coupon): void {
    navigator.clipboard?.writeText(coupon.code).then(
      () => this.toaster.showSuccess(`${coupon.code} copied.`, 'success'),
      () => undefined,
    );
  }

  get todayIso(): string {
    return new Date().toISOString().slice(0, 10);
  }

  // ---- numbers ------------------------------------------------------------

  /** "$16.00" — money from the API comes as a decimal string. */
  money(value: string | number | null | undefined): string {
    return `$${(Number(value) || 0).toFixed(2)}`;
  }

  get publishedCount(): number {
    return this.listings.filter((l) => l.status === 'published').length;
  }

  itemSales(listing: Listing): { sales: number; earnings: string; claims: number } {
    return this.stats?.per_listing?.[String(listing.id)] ?? { sales: 0, earnings: '0', claims: 0 };
  }

  get payoutsReady(): boolean {
    return !!this.connectStatus?.charges_enabled && !!this.connectStatus?.payouts_enabled;
  }

  /** Five drawn stars for a 1–5 rating. */
  starsFor(rating: number): boolean[] {
    return [1, 2, 3, 4, 5].map((n) => n <= Math.round(Number(rating) || 0));
  }

  // ---- shop profile ------------------------------------------------------

  /** Back to the saved profile (also the initial state). */
  resetProfile(): void {
    if (!this.vendor) return;
    this.revokePreviews();
    this.profileName = this.vendor.shop_name || '';
    this.profileBio = this.vendor.bio || '';
    this.logoPreview = this.vendor.logo || null;
    this.bannerPreview = this.vendor.banner || null;
    this.picked = {};
    this.cleared = { logo: false, banner: false };
  }

  get profileDirty(): boolean {
    if (!this.vendor) return false;
    return (
      this.profileName.trim() !== (this.vendor.shop_name || '') ||
      this.profileBio.trim() !== (this.vendor.bio || '') ||
      !!this.picked.logo || !!this.picked.banner ||
      this.cleared.logo || this.cleared.banner
    );
  }

  async pickPicture(kind: 'logo' | 'banner', event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const original = input.files?.[0];
    input.value = ''; // picking the same file again still fires (change)
    if (!original) return;
    if (!original.type.startsWith('image/')) {
      this.toaster.showNote("That file isn't a picture. Use a PNG, JPG or WebP image.", 'warning');
      return;
    }

    const seq = ++this.pickSeq[kind];
    this.preparing[kind] = true;
    this.cdr.detectChanges();

    let file: File | null = null;
    try {
      file = await this.withTimeout(this.fitPicture(original, kind), PICTURE_PREPARE_TIMEOUT_MS);
    } catch (err) {
      console.warn('[shop profile] could not shrink the picture, trying the original', err);
    }
    // Shrinking failed or took too long: the original still works if the server takes it.
    if (!file && PICTURE_SERVER_TYPES.includes(original.type) && original.size <= PICTURE_SERVER_MAX_BYTES) {
      file = original;
    }

    // Back inside Angular so the preview shows straight away (code after an
    // await can run outside Angular's change tracking).
    this.zone.run(() => {
      if (seq !== this.pickSeq[kind]) return; // a newer pick replaced this one
      this.preparing[kind] = false;
      if (!file) {
        this.toaster.showNote("This picture couldn't be used. Try a PNG or JPG under 5 MB.", 'warning');
      } else {
        this.revokePreview(kind);
        this.picked[kind] = file;
        this.cleared[kind] = false;
        const url = URL.createObjectURL(file);
        if (kind === 'logo') this.logoPreview = url;
        else this.bannerPreview = url;
      }
      this.cdr.detectChanges();
    });
  }

  /** Resolves null if the promise takes longer than `ms`. */
  private withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | null> {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => resolve(null), ms);
      promise.then(
        (value) => { clearTimeout(timer); resolve(value); },
        (err) => { clearTimeout(timer); reject(err); },
      );
    });
  }

  /**
   * Shrinks a picture to the size it's shown at and re-encodes it (WebP,
   * else JPEG), so large phone photos and formats the server doesn't take
   * still upload. Small PNG / JPG / WebP files are sent unchanged. Null when
   * the browser can't read the image.
   */
  private async fitPicture(file: File, kind: 'logo' | 'banner'): Promise<File | null> {
    const limit = PICTURE_LIMITS[kind];
    let bitmap: ImageBitmap;
    try {
      bitmap = await createImageBitmap(file);
    } catch {
      return null;
    }
    const scale = Math.min(1, limit.width / bitmap.width, limit.height / bitmap.height);
    const accepted = ['image/png', 'image/jpeg', 'image/webp'].includes(file.type);
    if (scale === 1 && accepted && file.size <= PICTURE_SEND_AS_IS_BYTES) {
      bitmap.close();
      return file;
    }

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext('2d')?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();

    const encode = (type: string) =>
      new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, 0.88));
    let blob = await encode('image/webp');
    let ext = 'webp';
    if (!blob || blob.type !== 'image/webp') {
      blob = await encode('image/jpeg'); // Safari can't write WebP
      ext = 'jpg';
    }
    if (!blob) return null;
    const base = (file.name.replace(/\.[^.]+$/, '') || kind).slice(0, 60);
    return new File([blob], `${base}.${ext}`, { type: blob.type });
  }

  removePicture(kind: 'logo' | 'banner'): void {
    this.revokePreview(kind);
    delete this.picked[kind];
    // Only ask the server to delete a picture that is actually saved.
    this.cleared[kind] = !!this.vendor?.[kind];
    if (kind === 'logo') this.logoPreview = null;
    else this.bannerPreview = null;
  }

  saveProfile(): void {
    if (!this.vendor || this.savingProfile || !this.profileDirty) return;
    const form = new FormData();
    form.append('shop_name', this.profileName.trim());
    form.append('bio', this.profileBio.trim());
    if (this.picked.logo) form.append('logo', this.picked.logo);
    if (this.picked.banner) form.append('banner', this.picked.banner);
    if (this.cleared.logo) form.append('clear_logo', 'true');
    if (this.cleared.banner) form.append('clear_banner', 'true');

    this.savingProfile = true;
    this.vendorService.updateMe(form).subscribe({
      next: (vendor) => {
        this.vendor = { ...this.vendor, ...vendor };
        this.savingProfile = false;
        this.resetProfile();
        this.toaster.showSuccess('Shop profile saved.', 'success');
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.savingProfile = false;
        if (err?.status === 401 || err?.status === 403) {
          this.toaster.showNote('Your sign-in has expired. Please sign in again, then save.', 'warning');
        } else {
          const body = err?.error || {};
          const detail = body.shop_name?.[0] || body.bio?.[0] || body.logo?.[0] || body.banner?.[0] || body.detail;
          this.toaster.showNote(detail || 'Could not save your shop profile.', 'danger');
        }
        this.cdr.detectChanges();
      },
    });
  }

  /** Free the in-memory preview of a picked (not yet saved) picture. */
  private revokePreview(kind: 'logo' | 'banner'): void {
    const url = kind === 'logo' ? this.logoPreview : this.bannerPreview;
    if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
  }

  private revokePreviews(): void {
    this.revokePreview('logo');
    this.revokePreview('banner');
  }

  // ---- reviews -----------------------------------------------------------

  /** Seeds the editor with any existing response so it reads as an edit. */
  startResponse(review: Review): void {
    if (this.responseDrafts[review.id] == null) {
      this.responseDrafts[review.id] = review.vendor_response || '';
    }
  }

  cancelResponse(review: Review): void {
    delete this.responseDrafts[review.id];
  }

  isResponding(review: Review): boolean {
    return this.responseDrafts[review.id] != null;
  }

  submitResponse(review: Review): void {
    const text = (this.responseDrafts[review.id] || '').trim();
    if (!text || this.respondingId === review.id) {
      return;
    }
    this.respondingId = review.id;

    this.reviewService.respond(review.id, text).subscribe({
      next: (updated) => {
        // Preview fixtures answer with an empty array rather than a Review, so
        // fall back to the text we just sent.
        this.reviews = this.reviews.map((r) =>
          r.id === review.id
            ? updated?.id
              ? updated
              : { ...r, vendor_response: text, vendor_responded_at: new Date().toISOString() }
            : r,
        );
        delete this.responseDrafts[review.id];
        this.respondingId = null;
        this.toaster.showSuccess('Response posted.', 'success');
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.respondingId = null;
        const detail = err?.error?.detail || err?.error?.[0];
        this.toaster.showNote(detail || 'Could not post your response.', 'danger');
        this.cdr.detectChanges();
      },
    });
  }

  // ---- items ---------------------------------------------------------------

  /**
   * Selling starts from the Content Vault: create the content there, then
   * use "Sell in store" on its page. (The bare listing form made store items
   * with no game fields or files that never appeared in the Content Vault.)
   */
  createListing(): void {
    this.router.navigate(['/add-edit-content']);
  }

  /** Content Vault listings are edited on their content page (where the Store panel is). */
  editListing(listing: Listing): void {
    if (listing.content) {
      this.router.navigate(['/add-edit-content'], {
        queryParams: { mode: 'edit', id: listing.content, citycode: 'null', isEditable: true },
      });
      return;
    }
    // Older listings not made from content keep the listing form.
    this.router.navigate(['/marketplace/vendor-dashboard/listings', listing.id, 'edit']);
  }

  statusLabel(listing: Listing): string {
    const labels: Record<string, string> = {
      published: 'In store',
      draft: 'Waiting for setup',
      delisted: 'Hidden',
    };
    return labels[listing.status] ?? listing.status;
  }
}
